// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface DashboardFilters {
  /** Single FY or comma-separated range (e.g., "2022,2023,2024") */
  fiscal_year?: number | string
  sub_agency_code?: string
  office_code?: string
  /**
   * Speed-to-contract only: select the FAR (false) or Other Transaction
   * (true) population. Absent → the endpoint defaults to FAR-only.
   */
  is_ot?: boolean
}

export interface SubAgencyOption {
  code: string
  name: string
}

export interface OfficeOption {
  code: string
  name: string
}

export interface FilterOptions {
  fiscal_years: number[]
  sub_agencies: SubAgencyOption[]
  offices?: OfficeOption[]
}

export interface GovdataResponse<T> {
  results: T[]
  filters: FilterOptions
}

export interface PaginatedGovdataResponse<T> extends GovdataResponse<T> {
  count: number
  page: number
  page_size: number
}

// ---------------------------------------------------------------------------
// Row types (one per mart table)
// ---------------------------------------------------------------------------

export interface OfficePerformance {
  awarding_agency_code: string
  awarding_agency_name: string
  awarding_sub_agency_code: string
  awarding_sub_agency_name: string
  awarding_office_code: string
  awarding_office_name: string
  funding_agency_code: string | null
  funding_agency_name: string | null
  funding_sub_agency_code: string | null
  funding_sub_agency_name: string | null
  funding_office_code: string | null
  funding_office_name: string | null
  fiscal_year: number
  fiscal_quarter: number
  // Combined totals
  total_awards: number
  total_obligated: string | null
  // FAR metrics
  total_far_awards: number | null
  far_obligated: string | null
  avg_far_obligation: string | null
  full_competition_count: number
  limited_competition_count: number
  sole_source_count: number
  gwac_idiq_count: number
  bpa_count: number
  standalone_contract_count: number
  // OT metrics
  total_ot_awards: number | null
  ot_obligated: string | null
  ot_agreement_count: number | null
  ot_idv_count: number | null
  ot_order_count: number | null
  ot_consortium_count: number | null
  ot_baa_count: number | null
  ot_sole_source_count: number | null
  avg_ot_duration_days: number | null
  ot_award_pct: number | null
  ot_dollar_pct: number | null
  // Small business
  small_biz_awards: number
  small_biz_dollars: string | null
  small_biz_pct: number | null
  set_aside_count: number
  count_8a: number
  count_hubzone: number
  count_sdvosb: number
  count_wosb: number
}

export interface CompetitionHealth {
  awarding_agency_code: string
  awarding_agency_name: string
  awarding_sub_agency_code: string
  awarding_sub_agency_name: string
  awarding_office_code: string
  awarding_office_name: string
  fiscal_year: number
  total_awards: number
  total_obligated: string | null
  full_open_count: number
  full_open_dollars: string | null
  full_open_pct: number | null
  limited_count: number
  limited_dollars: string | null
  not_competed_count: number
  not_competed_dollars: string | null
  not_competed_pct: number | null
  avg_offers_received: number | null
  median_offers_received: number | null
  single_offer_count: number
  single_offer_pct: number | null
}

export interface SpeedToContract {
  awarding_agency_code: string
  awarding_agency_name: string
  awarding_sub_agency_code: string
  awarding_sub_agency_name: string
  awarding_office_code: string
  awarding_office_name: string
  fiscal_year: number
  extent_competed: string | null
  idv_type: string | null
  award_count: number
  p25_solicitation_to_award: number | null
  p50_solicitation_to_award: number | null
  p75_solicitation_to_award: number | null
  p90_solicitation_to_award: number | null
  awards_with_solicitation_link: number | null
  // p*_pop_to_award removed: per FPDS, period_of_performance_start_date is
  // usually equal to date_signed, so the diff was meaningless.
}

export interface SmallBizEngagement {
  awarding_agency_code: string
  awarding_agency_name: string
  awarding_sub_agency_code: string
  awarding_sub_agency_name: string
  awarding_office_code: string
  awarding_office_name: string
  fiscal_year: number
  total_awards: number
  total_obligated: string | null
  sb_awards: number
  sb_dollars: string | null
  sb_dollar_pct: number | null
  count_8a: number
  dollars_8a: string | null
  count_hubzone: number
  dollars_hubzone: string | null
  count_sdvosb: number
  dollars_sdvosb: string | null
  count_wosb: number
  dollars_wosb: string | null
  set_aside_awards: number
  set_aside_pct: number | null
}

export interface ContractTypeTrend {
  awarding_agency_code: string
  awarding_agency_name: string
  awarding_sub_agency_code: string
  awarding_sub_agency_name: string
  awarding_office_code: string
  awarding_office_name: string
  fiscal_year: number
  fiscal_quarter: number
  vehicle_category: string | null
  pricing_category: string | null
  award_count: number
  total_obligated: string | null
  avg_obligation: string | null
}

export interface VendorConcentration {
  awarding_agency_code: string
  awarding_agency_name: string
  awarding_sub_agency_code: string
  awarding_sub_agency_name: string
  awarding_office_code: string
  awarding_office_name: string
  fiscal_year: number
  recipient_uei: string | null
  recipient_name: string | null
  award_count: number
  total_obligated: string | null
  first_award_date: string | null
  latest_award_date: string | null
  distinct_naics: number | null
  sb_awards: number | null
  dollar_rank: number
  dollar_share: string | null
  cumulative_share: string | null
}

export interface FiscalYearEndSurge {
  awarding_agency_code: string
  awarding_agency_name: string
  awarding_sub_agency_code: string
  awarding_sub_agency_name: string
  awarding_office_code: string
  awarding_office_name: string
  fiscal_year: number
  fiscal_month: number
  award_count: number
  total_obligated: string | null
  is_q4: boolean
  month_pct_of_fy: string | null
  q4_share_of_fy: string | null
}

export interface DimOffice {
  office_code: string
  office_name: string | null
  sub_agency_code: string | null
  sub_agency_name: string | null
  agency_code: string | null
  agency_name: string | null
  macom_code: string | null
  macom_name: string | null
  subcom1_code: string | null
  subcom1_name: string | null
  subcom2_code: string | null
  subcom2_name: string | null
  uses_other_transactions: boolean
}

export interface OtPrevalenceRollup {
  hierarchy_level: 'agency' | 'macom' | 'subcom1' | 'office'
  entity_code: string
  entity_name: string
  parent_code: string | null
  parent_name: string | null
  fiscal_year: number
  far_awards: number
  ot_awards: number
  total_awards: number
  far_obligated: string | null
  ot_obligated: string | null
  total_obligated: string | null
  ot_agreement_count: number
  ot_idv_count: number
  ot_order_count: number
  ot_consortium_count: number
  ot_baa_count: number
  ot_sole_source_count: number
  child_count: number
  ot_award_pct: number | null
  ot_dollar_pct: number | null
}

export interface OtPrevalenceFilters {
  fiscal_years: number[]
}

export interface OtPrevalenceResponse {
  results: OtPrevalenceRollup[]
  filters: OtPrevalenceFilters
}

export interface ContractDuration {
  awarding_agency_code: string
  awarding_agency_name: string
  awarding_sub_agency_code: string
  awarding_sub_agency_name: string
  awarding_office_code: string
  awarding_office_name: string
  fiscal_year: number
  vehicle_category: string | null
  pricing_category: string | null
  award_count: number
  total_obligated: string | null
  p25_duration_days: number | null
  p50_duration_days: number | null
  p75_duration_days: number | null
  p90_duration_days: number | null
  avg_duration_days: number | null
  min_duration_days: number | null
  max_duration_days: number | null
}

export type HierarchyLevel = 'agency' | 'macom' | 'subcom1' | 'office'

export interface AcqPerformanceRollup {
  hierarchy_level: HierarchyLevel
  entity_code: string
  entity_name: string
  parent_code: string | null
  parent_name: string | null
  fiscal_year: number
  // Volume
  total_awards: number
  total_obligated: number | null
  total_far_awards: number
  far_obligated: number | null
  total_ot_awards: number
  ot_obligated: number | null
  // Competition
  full_competition_count: number
  limited_competition_count: number
  sole_source_count: number
  // Vehicles
  gwac_idiq_count: number
  bpa_count: number
  standalone_contract_count: number
  ot_agreement_count: number
  ot_idv_count: number
  ot_order_count: number
  // OT solicitation methods
  ot_consortium_count: number
  ot_baa_count: number
  ot_sole_source_count: number
  // Small business
  small_biz_awards: number
  small_biz_dollars: number | null
  small_biz_pct: number | null
  count_8a: number
  count_hubzone: number
  count_sdvosb: number
  count_wosb: number
  set_aside_count: number
  // Computed ratios
  ot_award_pct: number | null
  ot_dollar_pct: number | null
  // Hierarchy
  child_count: number
}

export interface AcqPerformanceResponse {
  results: AcqPerformanceRollup[]
  filters: { fiscal_years: number[] }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse a string or number value to a number, returning 0 for null/undefined/empty. */
export function parseNum(val: string | number | null | undefined): number {
  if (val === null || val === undefined || val === '') return 0
  const n = typeof val === 'number' ? val : parseFloat(val)
  return isNaN(n) ? 0 : n
}

/** Format a large number with abbreviation (e.g., 1.2B, 450M, 12K). */
export function formatDollars(val: string | number | null | undefined): string {
  const n = parseNum(val)
  if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(1)}B`
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`
  return `$${n.toFixed(0)}`
}

/** Format a number with commas. */
export function formatNumber(val: number | null | undefined): string {
  if (val === null || val === undefined) return '0'
  return val.toLocaleString()
}

/** Format a percentage. */
export function formatPct(val: number | null | undefined, digits = 1): string {
  if (val === null || val === undefined) return '0%'
  return `${(val * 100).toFixed(digits)}%`
}
